import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export const FAQSection: React.FC = () => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<number[]>([]);

  const faqData = [
    {
      question: t('faq.questions.howItWorks.question'),
      answer: t('faq.questions.howItWorks.answer')
    },
    {
      question: t('faq.questions.compatibility.question'),
      answer: t('faq.questions.compatibility.answer')
    },
    {
      question: t('faq.questions.templates.question'),
      answer: t('faq.questions.templates.answer')
    },
    {
      question: t('faq.questions.tokenCounter.question'),
      answer: t('faq.questions.tokenCounter.answer')
    },
    {
      question: t('faq.questions.focusMode.question'),
      answer: t('faq.questions.focusMode.answer')
    },
    {
      question: t('faq.questions.autoSave.question'),
      answer: t('faq.questions.autoSave.answer')
    },
    {
      question: t('faq.questions.export.question'),
      answer: t('faq.questions.export.answer')
    },
    {
      question: t('faq.questions.free.question'),
      answer: t('faq.questions.free.answer')
    }
  ];

  const toggleItem = (index: number) => {
    setOpenItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <section className="max-w-4xl mx-auto py-16 px-6" aria-labelledby="faq-heading">
      <div className="text-center mb-12">
        <h2 id="faq-heading" className="text-2xl font-medium text-ghost-800 mb-4">
          {t('faq.title')}
        </h2>
        <p className="text-ghost-600 max-w-2xl mx-auto">
          {t('faq.subtitle')}
        </p>
      </div>

      <div className="space-y-4">
        {faqData.map((item, index) => (
          <div
            key={index}
            className="border border-ghost-200 rounded-lg overflow-hidden transition-all duration-200"
          >
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-ghost-50 transition-colors duration-150"
              aria-expanded={openItems.includes(index)}
              aria-controls={`faq-answer-${index}`}
            >
              <h3 className="font-medium text-ghost-800 pr-4">
                {item.question}
              </h3>
              <svg 
                className={`w-5 h-5 text-ghost-400 transform transition-transform duration-200 ${
                  openItems.includes(index) ? 'rotate-180' : ''
                }`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            <div
              id={`faq-answer-${index}`}
              className={`px-6 overflow-hidden transition-all duration-200 ${
                openItems.includes(index) ? 'max-h-96 pb-4' : 'max-h-0'
              }`}
            >
              <p className="text-ghost-600 leading-relaxed">
                {item.answer}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Structured data for FAQ */}
      <script 
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqData.map(item => ({
              "@type": "Question",
              "name": item.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": item.answer
              }
            }))
          })
        }}
      />
    </section>
  );
};